import { ArrowLeft, Info } from '@mui/icons-material'
import { Box, Button, Typography } from '@mui/material'
import React from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import MotivationalQuote from '../dashboard/MotivationalQuote'
import useMotivationalQuotes from '../hooks/api/useMotivationalQuotes'
import useIsMobile from '../hooks/useIsMobile'

import iconPageMap from '../nav/iconPageMap'
import Tooltip from './Tooltip'
import { NoMaxWidthTooltip } from '../customer/CustomerDetail'
import { SUPPORT_NUMBER } from '../constants'

const InfoIconMap = {
  '/dashboard/activity':
    'In this section you can track the outcomes of your goals.',
  // ---------------------
  '/dashboard/events': 'In this section, you can plan for your next event.',
  // ---------------------
  '/dashboard/strategy':
    'In this section, you can create your specific strategy in order to achieve your goals.',
  // ---------------------
  '/dashboard/documents':
    'In this section, you will have access to your company master documents.',
  // ---------------------
  '/customers':
    'In this section you can conveniently find accounts through preset filters.',
  // ---------------------
  '/cardwall': `In this section, you can select from 4 different pipelines: Opportunities,  Implementation, Marketing and Tickets.

    Unique cards can be created and assigned to certain stages within a pipeline. Key information about the account is added to the card.
    
    Cards can be dragged & dropped from one stage to another.
    
    You can filter the card wall by: the account owner, specific accounts, and the status of the current accounts.
    
    From the header at the top of each stage, you can sort the order of cards in different ways.
    
    In the opportunity: Double Click any card to view or edit the opportunity.
    
    Upon clicking the name of the account in the card, will automatically open up the corresponding account profile.
    
    Note: If you’re unable to see all the stages on your screen, use the scrollbar at the bottom of the page to scroll back and forth.
    
    Use Command + or - to zoom in or out on the screen

    Use Shift + Scrollwheel to navigate the cardwall quickly left to right
    `,
  // ---------------------
  '/lists': `In this section, you can customize a convenient working list of accounts that you would like to designate in a certain order.

    By selecting the buttons in both the Columns & Filters sections then applying the sorting options to your preference, you can magically generate a personalized working list!
    
    On the header of each category. Certain columns can be sorted in ascending  or decending order.
    
    By hovering your cursor to the far right of the manage lists screen. A slider will open giving you more information about the account.
    
    By clicking on the Account Name, you can go directly to that corresponding account profile.
    
    You can also export any data from the list that you created by clicking the export button on the top right portion of the screen.`,
  // ---------------------
  '/support': `In this section you can obtain an in depth knowledge-base of the entire StartUP CRM software by category.

    The content includes: Easy to follow videos & tutorials for fast learning.`,
  '/settings/*': `This section is designated for Administrators only.

    The settings section reflects the setup wizard process you went through when initially creating the account.

    The administrator has the ability to customize all sections of the system in behalf of your organization.
    
    If as an administrator you have any questions about setting up your database, refer to the support section, send an email to info@startupcrm.net or feel free to call our support team directly @ ${SUPPORT_NUMBER}`,
}

const PageTitle = ({ children, additionalText }) => {
  const [searchParams] = useSearchParams()

  const navigate = useNavigate()
  const location = useLocation()

  const customerDetailRegex = /\/customers\/\d+/

  const isOnCustomerDetailsPage = customerDetailRegex.test(location.pathname)

  const from = isOnCustomerDetailsPage
    ? 'customers?preventRedirect=true'
    : searchParams.get('from')

  const infoText =
    InfoIconMap[location.pathname] ||
    InfoIconMap['/' + location.pathname.split('/')[1] + '/*']

  const isMobile = useIsMobile()

  const rootPath = '/' + location.pathname.split('/')[1]

  const pageIcon = iconPageMap[rootPath]

  const {
    motivationalQuotes,
    displayLocation,
    isLoading,
    hasLoadError,
    saveMotivationalQuotes,
  } = useMotivationalQuotes({ skip: isMobile })

  const isDashboard = location.pathname.includes('/dashboard')
  const isSettings = location.pathname.includes('/settings')

  const isDashboardOnly = displayLocation === 'dashboard-only'
  const shouldRenderQuote =
    !isLoading &&
    !hasLoadError &&
    motivationalQuotes?.length > 0 &&
    (isDashboard || (!isDashboardOnly && !isSettings))

  return (
    <Box sx={{ mb: 1 }}>
      {from && (
        <Button
          variant={'contained'}
          onClick={() => navigate(`/${from}`)}
          sx={{ mr: 2, ml: 2, borderRadius: '8px', mb: 2 }}
        >
          {isOnCustomerDetailsPage ? 'All Accounts View' : 'Back'}
        </Button>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ ml: 2 }} variant={'h6'}>
            {children}
          </Typography>

          {pageIcon && <Box sx={{ ml: 1 }}>{pageIcon.icon}</Box>}

          {additionalText && (
            <Typography variant={'h6'} sx={{ ml: 1 }}>
              {additionalText}
            </Typography>
          )}

          {infoText && (
            <NoMaxWidthTooltip
              title={
                <Typography
                  sx={{
                    whiteSpace: 'pre-line',
                    width: '400px',
                  }}
                >
                  {infoText}
                </Typography>
              }
            >
              <Info sx={{ ml: 2 }} color='primary'></Info>
            </NoMaxWidthTooltip>
          )}
        </Box>
        {shouldRenderQuote && (
          <Box sx={{ ml: 4 }}>
            <MotivationalQuote
              motivationalQuotes={motivationalQuotes}
              saveMotivationalQuotes={saveMotivationalQuotes}
            />
          </Box>
        )}
        <Box></Box>
      </Box>
    </Box>
  )
}

export default PageTitle
